import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { jourLocal, aujourdhui, debutDuMois, finDuMois } from '../services/dates';

const fmt = (n) => n == null ? '—' : Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const fmtN = (n) => n == null ? '—' : Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MOIS_FR = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
const fmtDate = (s) => { if (!s) return '—'; const [y,m,d] = s.split('-'); return `${d} ${MOIS_FR[parseInt(m,10)-1]} ${y}`; };

const RESULTAT_STYLE = {
  CLEARED:  { bg: 'rgba(34,197,94,0.12)',  color: '#16a34a', label: 'CLEARED' },
  FAILED:   { bg: 'rgba(239,68,68,0.10)',  color: '#dc2626', label: 'FAILED'  },
  en_cours: { bg: 'rgba(245,158,11,0.12)', color: '#b45309', label: 'En cours' },
};

/**
 * La liste derrière un chiffre.
 *
 * Un total ne se vérifie pas : il faut voir les lignes. On y met de quoi
 * retrouver la commande dans Zoho et de quoi comprendre le prix — l'acte, le
 * devis et la plus-value séparés, la clôture à côté du jour d'intervention —
 * plus un bouton qui copie les références, parce que la correction se fait
 * ailleurs, commande par commande.
 */
function FenetreDetail({ detail, onClose }) {
  const [copie, setCopie] = React.useState(false);
  const [excel, setExcel] = React.useState(false);

  /**
   * La liste, en classeur.
   *
   * Ces listes sont filtrees ici, dans le navigateur, par une fonction : le
   * serveur ne peut pas reconstruire le filtre. On lui envoie donc les lignes
   * affichees, et il les met en forme — la feuille dit exactement ce que la
   * fenetre dit, ce qui est la condition pour la confronter au releve de
   * Covage ligne par ligne.
   */
  const exporter = async () => {
    if (!detail || !detail.lignes.length) return;
    setExcel(true);
    try {
      const r = await api.post('/admin/remunerations/detail.xlsx', {
        lignes: detail.lignes, titre: detail.titre, note: detail.note,
        debut: detail.debut, fin: detail.fin,
      }, { responseType: 'blob' });
      // Le nom du fichier est celui que le serveur a pose dans l'en-tete.
      const trouve = /filename="([^"]+)"/.exec(r.headers['content-disposition'] || '');
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = trouve ? trouve[1] : 'detail.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // En « responseType: blob », le message d'erreur du serveur arrive en
      // blob lui aussi : sans le relire on n'affiche que « status code 500 ».
      let m = e.message;
      try {
        if (e.response?.data instanceof Blob) {
          m = JSON.parse(await e.response.data.text()).error || m;
        }
      } catch { /* le serveur n'a pas repondu en JSON : on garde son message */ }
      alert('Export impossible : ' + m);
    } finally {
      setExcel(false);
    }
  };

  if (!detail) return null;

  const { titre, note, lignes } = detail;
  const tot = lignes.reduce((a, it) => ({
    acte:  a.acte  + (it.is_duplicate ? 0 : (it.prix_rac_covage || 0)),
    devis: a.devis + (it.is_duplicate ? 0 : (it.montant_devis   || 0)),
    fpv:   a.fpv   + (it.is_duplicate ? 0 : (it.prix_fpv_covage || 0)),
    cout:  a.cout  + (it.is_duplicate ? 0 : (it.prix_tech       || 0)),
  }), { acte: 0, devis: 0, fpv: 0, cout: 0 });

  const refs = [...new Set(lignes.map(l => l.ref_commande).filter(Boolean))];
  const copier = () => {
    const texte = refs.join('\n');
    const fini = () => { setCopie(true); setTimeout(() => setCopie(false), 2000); };
    if (navigator.clipboard) navigator.clipboard.writeText(texte).then(fini, () => {});
    else {
      const ta = document.createElement('textarea');
      ta.value = texte; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); fini(); } finally { document.body.removeChild(ta); }
    }
  };

  const th = { padding: '7px 10px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em',
    color: 'var(--text3)', textAlign: 'right', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: 'var(--bg3)' };
  const td = { padding: '6px 10px', fontSize: 12, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
  const mono = { fontFamily: 'var(--mono)', fontSize: 11 };

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 900,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} className="card"
        style={{ width: 'min(1180px, 100%)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', gap: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{titre}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>
              {lignes.length} ligne{lignes.length > 1 ? 's' : ''}
              {note ? ' — ' + note : ''}
            </div>
          </div>
          {refs.length > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={copier}>
              {copie ? '✓ Copié' : `Copier les ${refs.length} référence${refs.length > 1 ? 's' : ''}`}
            </button>
          )}
          {/* La feuille sert a confronter cette liste au releve de Covage :
              references en premiere colonne, devis du CRM et du compte rendu
              cote a cote, et l'ecart surligne. */}
          {lignes.length > 0 && (
            <button className="btn btn-sm" onClick={exporter} disabled={excel}
              title="Télécharger cette liste en classeur Excel, pour la comparer au relevé de Covage"
              style={{ background: '#15803d', color: '#fff', border: 'none' }}>
              {excel ? 'Export…' : 'Excel'}
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Fermer</button>
        </div>

        {lignes.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
            Aucune ligne sur cette période.
          </div>
        ) : (
          <div style={{ overflow: 'auto', flex: 1, border: '1px solid var(--border)', borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg3)' }}>
                  <th style={{ ...th, textAlign: 'left' }}>Clôturé le</th>
                  <th style={{ ...th, textAlign: 'left' }}>Intervention</th>
                  <th style={{ ...th, textAlign: 'left' }}>Compte</th>
                  <th style={{ ...th, textAlign: 'left' }}>Commande</th>
                  <th style={{ ...th, textAlign: 'left' }}>Work order</th>
                  <th style={{ ...th, textAlign: 'left' }}>Typologie</th>
                  <th style={th}>Acte</th>
                  <th style={th}>Devis</th>
                  <th style={th}>Plus-value</th>
                  <th style={th}>Coût tech.</th>
                  <th style={{ ...th, textAlign: 'left' }}>État</th>
                </tr>
              </thead>
              <tbody>
                {lignes.map((it, i) => {
                  const rs = RESULTAT_STYLE[it.resultat] || RESULTAT_STYLE.en_cours;
                  const ecarte = it.is_duplicate;
                  return (
                    <tr key={i} style={{ borderTop: '1px solid var(--border)',
                      background: ecarte ? 'rgba(245,158,11,0.07)' : i % 2 ? 'var(--bg2)' : 'transparent',
                      opacity: ecarte ? 0.65 : 1 }}>
                      <td style={{ ...td, textAlign: 'left', ...mono }}>{fmtDate(it.date_cloture || it.date)}</td>
                      <td style={{ ...td, textAlign: 'left', ...mono, color: 'var(--text3)' }}>
                        {it.date_depot && it.date_depot !== (it.date_cloture || it.date) ? fmtDate(it.date_depot) : '—'}
                      </td>
                      <td style={{ ...td, textAlign: 'left' }}>
                        <span style={{ ...mono, fontWeight: 700 }}>{it.ref_compte || '—'}</span>
                        {it.technicien && <div style={{ fontSize: 10, color: 'var(--text3)' }}>{it.technicien}</div>}
                      </td>
                      {/* La fiche du dossier s'ouvre à côté : on relit la
                          ligne sans perdre la liste qu'on est en train de
                          dépouiller. */}
                      <td style={{ ...td, textAlign: 'left', ...mono }}>
                        {it.dossier_id ? (
                          <a href={`/dossier/${it.dossier_id}`} target="_blank" rel="noreferrer"
                            title="Ouvrir la fiche du dossier dans un nouvel onglet"
                            style={{ color: 'var(--blue2)', textDecoration: 'none', borderBottom: '1px dotted var(--blue2)' }}>
                            {it.ref_commande || 'fiche'}
                          </a>
                        ) : (
                          <span style={{ color: 'var(--blue2)' }}>{it.ref_commande || '—'}</span>
                        )}
                      </td>
                      {/* Le W de la clôture qui a facturé, et celui que la
                          commande porte aujourd'hui quand il a changé : sans
                          lui, la ligne est introuvable pour qui cherche la
                          référence que Zoho affiche ou que Covage cite. */}
                      <td style={{ ...td, textAlign: 'left', ...mono, color: 'var(--text3)' }}>
                        {it.ref_work_order || '—'}
                        {it.ref_work_order_zoho && (
                          <div title={'La commande porte aujourd’hui ' + it.ref_work_order_zoho
                            + ' : elle est repassée en intervention depuis cette clôture. C’est '
                            + (it.ref_work_order || 'celui-ci') + ' qui a facturé.'}
                            style={{ fontSize: 9.5, color: '#b45309', fontWeight: 700 }}>
                            auj. {it.ref_work_order_zoho}
                          </div>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'left' }}>
                        {it.typo_label || it.typo || '—'}
                        {it.type_itv && <div style={{ fontSize: 10, color: 'var(--text3)' }}>{it.type_itv}</div>}
                      </td>
                      <td style={td}>{it.prix_rac_covage ? fmt(it.prix_rac_covage) : '—'}</td>
                      <td style={td}>
                        {it.montant_devis ? fmt(it.montant_devis)
                          : it.devis_sans_montant ? 'coché, sans montant' : '—'}
                        {/* Le compte rendu disait le contraire du CRM. C'est le CRM
                            qui facture, mais l'ecart merite un oeil. */}
                        {!!it.is_devis !== !!it.devis_cr && (
                          <span title={it.is_devis
                            ? 'Devis pose dans le CRM, que le technicien n avait pas coche. C est le CRM qui facture.'
                            : 'Coche par le technicien, pas pose dans le CRM : il n est pas facture.'}
                            style={{ marginLeft: 5, fontSize: 9, fontWeight: 800, color: '#b45309' }}>≠ CR</span>
                        )}
                      </td>
                      <td style={{ ...td, fontWeight: it.prix_fpv_covage ? 700 : 400,
                        color: it.prix_fpv_covage ? '#b45309' : 'var(--text3)' }}>
                        {it.prix_fpv_covage ? fmt(it.prix_fpv_covage) : it.has_fpv ? 'coché, non facturé' : '—'}
                        {/* Le compte rendu disait le contraire du CRM. C'est
                            le CRM qui facture, mais l'ecart merite un oeil. */}
                        {!!it.has_fpv !== !!it.fpv_cr && (
                          <span title={it.has_fpv
                            ? 'Posee dans le CRM, que le technicien n avait pas cochee. C est le CRM qui facture.'
                            : 'Cochee par le technicien, pas posee dans le CRM : elle n est pas facturee.'}
                            style={{ marginLeft: 5, fontSize: 9, fontWeight: 800, color: '#b45309' }}>≠ CR</span>
                        )}
                      </td>
                      <td style={{ ...td, color: '#f59e0b' }}>{it.prix_tech ? fmt(it.prix_tech) : '—'}</td>
                      <td style={{ ...td, textAlign: 'left' }}>
                        <span style={{ background: rs.bg, color: rs.color, borderRadius: 6, padding: '2px 7px', fontSize: 10, fontWeight: 700 }}>{rs.label}</span>
                        {ecarte && (
                          <span style={{ marginLeft: 5, background: 'rgba(245,158,11,0.2)', color: '#b45309',
                            borderRadius: 5, padding: '1px 5px', fontSize: 9, fontWeight: 800 }}>
                            {it.vt_remplacee ? 'VT NON DUE' : it.doublon_commande ? 'DEJA FACTUREE' : 'DOUBLON'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--bg3)', borderTop: '2px solid var(--border)' }}>
                  <td colSpan={6} style={{ ...td, textAlign: 'left', fontWeight: 700 }}>Total facturable</td>
                  <td style={{ ...td, fontWeight: 700 }}>{fmt(tot.acte)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{fmt(tot.devis)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{fmt(tot.fpv)}</td>
                  <td style={{ ...td, fontWeight: 700, color: '#f59e0b' }}>{fmt(tot.cout)}</td>
                  <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>
                    {fmt(tot.acte + tot.devis + tot.fpv)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/** Le petit bouton qui ouvre la liste derrière un chiffre. */
function BtnListe({ onClick, titre = 'Voir la liste' }) {
  return (
    <button onClick={onClick} title={titre}
      style={{ marginLeft: 8, border: '1px solid var(--border)', background: 'var(--bg3)',
        color: 'var(--text2)', borderRadius: 6, padding: '1px 7px', fontSize: 10, fontWeight: 700,
        cursor: 'pointer', whiteSpace: 'nowrap' }}>
      voir
    </button>
  );
}

export default function Remunerations() {
  const [tab, setTab] = useState('rapport');

  // Données de base
  // Les comptes à payer, fermés compris : un compte D3_xx est un planning
  // confié à une société, et ce qu'elle a fait reste dû même si le compte a
  // changé de main depuis. users.json ne connait que les comptes ouverts du
  // jour, d'où des mois entiers introuvables au filtre.
  const [comptes, setComptes] = useState([]);
  const [tarifs, setTarifs]           = useState({});
  const [savingTarifs, setSavingTarifs] = useState(false);
  const [tarifsMsg, setTarifsMsg]     = useState('');

  // Filtres rapport
  const [selTechs, setSelTechs]   = useState([]);
  // En heure locale, jamais par toISOString : voir services/dates.js. Le 1er
  // du mois ecrit en UTC tombe le dernier jour du mois precedent.
  const [dateDebut, setDateDebut] = useState(debutDuMois);
  const [dateFin, setDateFin] = useState(aujourdhui);

  // Résultats
  const [items,   setItems]   = useState(null);
  const [summary, setSummary] = useState(null);
  // Ce que le rapport compte à part ou ne sait pas compter : devis, typologies
  // sans tarif, doublons, changements de titulaire. Mieux vaut le lire.
  const [signaux, setSignaux] = useState(null);
  // Combien de RAC_I, de RAC_C, de SAV 24 h : le rapport comptait l argent
  // sans jamais dire les volumes, et c est la premiere chose qu on verifie.
  const [typologies, setTypologies] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState('');

  // Filtre compte dans le tableau détail
  const [filterCompte, setFilterCompte] = useState('');
  const [filterResultat, setFilterResultat] = useState('');

  // La liste derrière un chiffre. Un carré, une ligne de typologie ou un
  // avertissement ne dit qu'un total : pour corriger une plus-value posée par
  // erreur sur deux PTO_C, il faut les commandes, pas le compte.
  const [detail, setDetail] = useState(null);

  // Tarifs locaux (pour l'édition)
  const [localTarifs, setLocalTarifs] = useState({});

  useEffect(() => {
    api.get('/admin/remunerations/techniciens').then(r => setComptes(r.data.comptes || [])).catch(() => {});
    api.get('/admin/tarifs').then(r => { setTarifs(r.data); setLocalTarifs(r.data); }).catch(() => {});
  }, []);

  const toggleCompte = (compte) => {
    setSelTechs(prev => prev.includes(compte) ? prev.filter(t => t !== compte) : [...prev, compte]);
  };

  const selectAll = () => setSelTechs(comptes.filter(c => c.actif).map(c => c.compte));
  const clearAll  = () => setSelTechs([]);

  const ouverts = comptes.filter(c => c.actif);
  const fermes  = comptes.filter(c => !c.actif && c.nb > 0);

  // Les entreprises, telles qu'elles sont saisies sur la page Techniciens.
  // Un compte sans entreprise n'apparaît pas ici : il reste accessible par
  // son numéro, mais il n'y a rien à regrouper.
  const entreprises = (() => {
    const m = new Map();
    for (const c of comptes) {
      const e = (c.entreprise || '').trim();
      if (!e) continue;
      if (!m.has(e)) m.set(e, []);
      m.get(e).push(c.compte);
    }
    return [...m.entries()]
      .map(([nom, liste]) => ({ nom, liste }))
      .sort((a, b) => b.liste.length - a.liste.length || a.nom.localeCompare(b.nom, 'fr'));
  })();

  // Cliquer une entreprise coche ses comptes ; si tous l'étaient déjà, les
  // décoche. On ne vide pas la sélection des autres : deux sociétés peuvent
  // se regarder ensemble.
  const toggleEntreprise = (ent) => {
    const tous = ent.liste.every(c => selTechs.includes(c));
    setSelTechs(prev => tous
      ? prev.filter(c => !ent.liste.includes(c))
      : [...new Set([...prev, ...ent.liste])]);
  };

  // Les sociétés qui ont des lignes dans le résultat courant, avec leur coût :
  // on ne propose pas un classeur vide, et le montant permet de reconnaître
  // celui qu'on cherche avant de le télécharger.
  const entreprisesDuResultat = (() => {
    if (!items || !summary) return [];
    const presents = new Set(items.map(it => it.compte));
    const netDe = {};
    for (const s of summary) netDe[s.compte] = s.net || 0;
    return entreprises
      .map(e => {
        const liste = e.liste.filter(c => presents.has(c));
        return { nom: e.nom, liste, net: liste.reduce((a, c) => a + (netDe[c] || 0), 0) };
      })
      .filter(e => e.liste.length > 0);
  })();

  // Les comptes du résultat qu'aucune entreprise ne réclame : ils ne sont que
  // dans le classeur global, et c'est la page Techniciens qui les rattache.
  const comptesSansEntreprise = (() => {
    if (!items) return [];
    const pris = new Set(entreprisesDuResultat.flatMap(e => e.liste));
    return [...new Set(items.map(it => it.compte))].filter(c => !pris.has(c));
  })();

  // Items filtrés pour le tableau détail
  const filteredItems = (items || []).filter(it => {
    if (filterCompte && it.compte !== filterCompte) return false;
    if (filterResultat === 'doublon' && !it.is_duplicate) return false;
    if (filterResultat && filterResultat !== 'doublon' && it.resultat !== filterResultat) return false;
    return true;
  });

  const nbDoublons = (items || []).filter(it => it.is_duplicate).length;

  /**
   * L'attachement, en classeur Excel.
   *
   * Le fichier se construit sur le serveur : c'est là que sont les données, et
   * un vrai classeur demande des montants stockés comme nombres, un filtre
   * automatique sur chaque colonne et un total en SOUS.TOTAL — qui ne compte
   * que les lignes laissées visibles par le filtre. Un CSV ne sait rien faire
   * de tout cela.
   *
   * Les deux filtres de l'écran partent avec la requête, pour que la feuille
   * montre ce que la page montrait.
   */
  const [exportEnCours, setExportEnCours] = useState(false);
  // L'entreprise en cours de téléchargement, pour n'allumer qu'un bouton.
  const [exportCible, setExportCible] = useState(null);

  /**
   * `cible` est une société : le classeur ne porte alors que ses comptes, et
   * son nom part avec la requête pour nommer le fichier. Sans cible, c'est le
   * classeur global de la recherche. Le filtre d'un compte précis ne
   * s'applique pas à un classeur de société : il le viderait.
   */
  const exporterXlsx = useCallback(async (cible = null) => {
    if (!items || filteredItems.length === 0) return;
    setExportEnCours(true);
    setExportCible(cible ? cible.nom : null);
    try {
      const r = await api.get('/admin/remunerations/export.xlsx', {
        responseType: 'blob',
        params: {
          dateDebut, dateFin,
          techniciens: cible ? cible.liste.join(',')
            : (selTechs.length ? selTechs.join(',') : undefined),
          entreprise: cible ? cible.nom : undefined,
          compte: cible ? undefined : (filterCompte || undefined),
          resultat: filterResultat || undefined,
        },
      });
      // Le nom du fichier est celui que le serveur a posé dans l'en-tête :
      // il connaît la période et le compte, inutile de le recalculer ici.
      const dispo = r.headers['content-disposition'] || '';
      const trouve = /filename="([^"]+)"/.exec(dispo);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = trouve ? trouve[1] : `attachement_${dateDebut}_${dateFin}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export impossible : ' + (e.response?.data?.error || e.message));
    } finally {
      setExportEnCours(false);
      setExportCible(null);
    }
  }, [items, filteredItems, selTechs, filterCompte, filterResultat, dateDebut, dateFin]);

  const compute = useCallback(async () => {
    if (!dateDebut || !dateFin) { setErr('Sélectionnez une période'); return; }
    setLoading(true); setErr(''); setItems(null); setSummary(null); setSignaux(null); setTypologies(null);
    try {
      const params = { dateDebut, dateFin };
      if (selTechs.length > 0) params.techniciens = selTechs.join(',');
      const r = await api.get('/admin/remunerations', { params });
      setItems(r.data.items || []);
      setSummary(r.data.summary || []);
      setSignaux(r.data.signaux || null);
      setTypologies(r.data.typologies || []);
    } catch (e) {
      setErr(e.response?.data?.error || 'Erreur de calcul');
    } finally {
      setLoading(false);
    }
  }, [dateDebut, dateFin, selTechs]);

  const saveTarifs = async () => {
    setSavingTarifs(true); setTarifsMsg('');
    try {
      await api.put('/admin/tarifs', localTarifs);
      setTarifs(localTarifs);
      setTarifsMsg('Tarifs enregistrés ✓');
      setTimeout(() => setTarifsMsg(''), 3000);
    } catch {
      setTarifsMsg('Erreur lors de la sauvegarde');
    } finally {
      setSavingTarifs(false);
    }
  };

  const updateLocalTarif = (key, field, value) => {
    setLocalTarifs(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: parseFloat(value) || 0 },
    }));
  };

  // Calculs globaux
  const totaux = summary ? {
    nb_total:  summary.reduce((s, t) => s + t.total,   0),
    nb_cleared:summary.reduce((s, t) => s + t.cleared, 0),
    revenu:    summary.reduce((s, t) => s + t.revenu,  0),
    cout:      summary.reduce((s, t) => s + t.cout,    0),
    marge:     summary.reduce((s, t) => s + t.marge,   0),
    // Ce que les comptes doivent, et ce qui leur reste à verser.
    malus:     summary.reduce((s, t) => s + (t.malus || 0), 0),
    net:       summary.reduce((s, t) => s + (t.net !== undefined ? t.net : t.cout), 0),
  } : null;

  // Breakdown par catégorie.
  //
  // Le regroupement suit la FAMILLE de l'intervention, pas sa typologie : un
  // « RACC suite SAV (RAC_C) » se paie au prix du RAC_C mais c'est un SAV, et
  // il manquait au carré SAV. Et les trois premières catégories ne comptent
  // que le prix de l'acte et la plus-value, la quatrième les devis : les
  // quatre carrés s'additionnent alors exactement au revenu total, au lieu de
  // compter deux fois un raccordement avec devis.
  const CATEGORIES = {
    prod:  { label: 'Production',       f: it => it.famille === 'RAC', acte: true },
    sav:   { label: 'SAV',              f: it => it.famille === 'SAV', acte: true },
    vt:    { label: 'Visite technique', f: it => it.famille === 'VT',  acte: true },
    devis: { label: 'Devis',            f: it => it.montant_devis > 0, acte: false },
  };
  const breakdown = items ? (() => {
    const nonDup = items.filter(it => !it.is_duplicate);
    const out = {};
    for (const [key, c] of Object.entries(CATEGORIES)) {
      const arr = nonDup.filter(c.f);
      out[key] = {
        count:  arr.length,
        // Une intervention qui a échoué reste une intervention, mais elle ne
        // se facture pas : sans ce second compte, un carré à 85 lignes et
        // 6 005 € se lit comme 70 € pièce.
        payees: arr.filter(it => it.resultat === 'CLEARED').length,
        revenu: arr.reduce((s, it) => s + (c.acte ? (it.prix_rac_covage || 0) + (it.prix_fpv_covage || 0) : (it.montant_devis || 0)), 0),
        cout:   arr.reduce((s, it) => s + (c.acte ? (it.prix_rac_tech   || 0) + (it.prix_fpv_tech   || 0) : (it.devis_tech    || 0)), 0),
      };
    }
    return out;
  })() : null;

  /**
   * Ouvre la liste derrière un chiffre. `note` dit d'où vient le chiffre, pour
   * qu'on relise la liste en sachant ce qu'elle est censée contenir.
   */
  /** Facturable et non ecartee : la meme regle que le serveur. */
  const paye = it => !it.is_duplicate && it.resultat === 'CLEARED';

  const ouvrirDetail = (titre, note, filtre) => setDetail({
    titre, note, lignes: (items || []).filter(filtre),
    // La periode part avec la liste : le classeur doit dire sur quoi il porte.
    debut: dateDebut, fin: dateFin,
  });

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">Rémunérations techniciens</div>
      </div>

      <div className="page">
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid var(--border)', paddingBottom: 0 }}>
          {[
            { key: 'rapport', label: '📊 Rapport' },
            { key: 'tarifs',  label: '⚙️ Tarifs' },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{
                padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
                fontWeight: 700, fontSize: 13,
                color: tab === t.key ? 'var(--blue2)' : 'var(--text3)',
                borderBottom: tab === t.key ? '2px solid var(--blue2)' : '2px solid transparent',
                marginBottom: -2,
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ─── TAB RAPPORT ─── */}
        {tab === 'rapport' && (
          <>
            {/* Filtres */}
            <div className="card" style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>Paramètres du rapport</div>

              {/* Techniciens */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', minWidth: 80 }}>Comptes</span>
                  <button className="btn btn-ghost btn-sm" onClick={selectAll}>Tous</button>
                  <button className="btn btn-ghost btn-sm" onClick={clearAll}>Aucun</button>
                  <span style={{ fontSize: 12, color: 'var(--text3)' }}>
                    {selTechs.length === 0 ? 'Tous les comptes' : `${selTechs.length} sélectionné(s)`}
                  </span>
                </div>
                {entreprises.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: 'var(--text3)', margin: '0 0 6px', fontWeight: 600 }}>
                      Par entreprise — une facture se fait par société, pas par compte
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {entreprises.map(ent => {
                        const tous = ent.liste.every(c => selTechs.includes(c));
                        const partiel = !tous && ent.liste.some(c => selTechs.includes(c));
                        return (
                          <button key={ent.nom} onClick={() => toggleEntreprise(ent)}
                            title={ent.liste.map(c => String(c).replace('VALEA-', '')).join(', ')}
                            style={{
                              padding: '6px 14px', borderRadius: 8,
                              border: `2px solid ${tous ? 'var(--green)' : partiel ? 'var(--amber)' : 'var(--border)'}`,
                              background: tous ? 'rgba(34,197,94,0.12)' : partiel ? 'rgba(245,158,11,0.10)' : 'var(--bg3)',
                              color: tous ? 'var(--green)' : partiel ? 'var(--amber)' : 'var(--text2)',
                              fontWeight: tous || partiel ? 700 : 400, fontSize: 12, cursor: 'pointer',
                            }}>
                            {ent.nom}
                            <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text3)' }}>
                              {ent.liste.length} compte{ent.liste.length > 1 ? 's' : ''}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {[{ cle: 'ouverts', titre: null, liste: ouverts },
                  { cle: 'fermes', titre: 'Comptes fermés — leur travail reste à payer', liste: fermes }]
                  .filter(g => g.liste.length > 0).map(g => (
                  <div key={g.cle} style={{ marginBottom: g.cle === 'ouverts' ? 10 : 0 }}>
                    {g.titre && (
                      <div style={{ fontSize: 11, color: 'var(--text3)', margin: '4px 0 6px', fontWeight: 600 }}>{g.titre}</div>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {g.liste.map(cc => {
                        const sel = selTechs.includes(cc.compte);
                        const noms = (cc.titulaires || []).map(t => t.nom);
                        const qui = cc.titulaire_actuel || noms[noms.length - 1] || '';
                        return (
                          <button key={cc.compte} onClick={() => toggleCompte(cc.compte)}
                            title={(noms.length > 1
                              ? 'Tenu par ' + (cc.titulaires || []).map(t => t.nom + ' (du ' + t.du + ' au ' + t.au + ')').join(', puis ')
                              : qui ? 'Tenu par ' + qui : '')
                              + (cc.nb ? ' — ' + cc.nb + ' compte(s) rendu(s)' : ' — aucun compte rendu')}
                            style={{
                              padding: '6px 14px', borderRadius: 20, border: `2px solid ${sel ? 'var(--blue2)' : 'var(--border)'}`,
                              background: sel ? 'rgba(59,130,246,0.12)' : 'var(--bg3)',
                              color: sel ? 'var(--blue2)' : 'var(--text2)',
                              fontWeight: sel ? 700 : 400, fontSize: 12, cursor: 'pointer',
                              opacity: g.cle === 'fermes' && !sel ? 0.75 : 1,
                            }}>
                            <span style={{ fontFamily: 'var(--mono)', fontWeight: 700 }}>{cc.ref}</span>
                            {qui && (
                              <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text3)' }}>
                                {qui}{noms.length > 1 ? ' +' + (noms.length - 1) : ''}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Période */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', minWidth: 80 }}
                  title="La période porte sur la date de clôture, celle que Covage retient pour facturer. Une intervention du dernier jour du mois clôturée le lendemain bascule donc sur le mois suivant.">
                  Période
                  <span style={{ display: 'block', fontSize: 10, fontWeight: 400, color: 'var(--text3)' }}>à la clôture</span>
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: 12, color: 'var(--text3)' }}>Du</label>
                  <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)}
                    className="input" style={{ width: 150, fontSize: 13 }} />
                  <label style={{ fontSize: 12, color: 'var(--text3)' }}>Au</label>
                  <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)}
                    className="input" style={{ width: 150, fontSize: 13 }} />
                </div>

                {/* Raccourcis période */}
                {[
                  { lbl: 'Ce mois',      fn: () => { setDateDebut(debutDuMois()); setDateFin(aujourdhui()); }},
                  // Le dernier jour du mois se perdait ici : « new Date(a, m+1, 0) »
                  // donne bien le 30 septembre, mais a minuit heure locale — soit le
                  // 29 a 22 h en UTC, et toISOString ecrivait donc le 29. Septembre
                  // sortait sans ses 22 dernieres lignes ni 4 398,90 € de revenu.
                  { lbl: 'Mois dernier', fn: () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth()-1); setDateDebut(jourLocal(d)); setDateFin(finDuMois(d)); }},
                  { lbl: 'Cette année',  fn: () => { setDateDebut(new Date().getFullYear() + '-01-01'); setDateFin(aujourdhui()); }},
                ].map(s => (
                  <button key={s.lbl} className="btn btn-ghost btn-sm" onClick={s.fn}>{s.lbl}</button>
                ))}

                <button className="btn btn-primary" onClick={compute} disabled={loading}
                  style={{ marginLeft: 8, minWidth: 120 }}>
                  {loading ? 'Calcul…' : '📊 Calculer'}
                </button>
              </div>
              {err && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>{err}</div>}
            </div>

            {/* Résultats */}
            {loading && (
              <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>
                Analyse des interventions en cours…
              </div>
            )}

            {!loading && summary && (
              <>
                {/* Un compte passé d'un technicien à l'autre dans le mois :
                    même société, donc même attachement. Ce n'est pas un
                    avertissement, c'est de quoi relire une ligne sans se
                    demander qui était là ce jour-là. */}
                {signaux && signaux.comptes_repris && signaux.comptes_repris.length > 0 && (
                  <div style={{ marginBottom: 20, padding: '12px 16px', borderRadius: 10,
                    background: 'var(--bg3)', border: '1px solid var(--border)' }}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>
                      Comptes passés d'un technicien à l'autre sur la période
                    </div>
                    {signaux.comptes_repris.map(c => (
                      <div key={c.compte} style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>
                        <strong style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>{c.ref}</strong>
                        {' : '}
                        {c.titulaires.map((t, i) => (
                          <span key={t.nom}>
                            {i > 0 && ', puis '}
                            {t.nom} {'(du ' + t.du + ' au ' + t.au + ')'}
                          </span>
                        ))}
                        {'. Le planning est le même : une seule ligne, un seul attachement.'}
                      </div>
                    ))}
                  </div>
                )}

                {/* Totaux globaux */}
                {totaux && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
                    {[
                      { lbl: 'Interventions', val: totaux.nb_total, sub: `dont ${totaux.nb_cleared} CLEARED`, color: 'var(--text)' },
                      { lbl: 'Revenu Covage', val: fmt(totaux.revenu), color: 'var(--blue2)' },
                      { lbl: 'Coût techniciens', val: fmt(totaux.cout), color: '#f59e0b' },
                      ...(totaux.malus ? [{ lbl: 'Malus retenus', val: '− ' + fmt(totaux.malus), color: '#ef4444' },
                        { lbl: 'Net à verser', val: fmt(totaux.net), color: '#f59e0b' }] : []),
                      { lbl: 'Marge nette', val: fmt(totaux.marge), color: totaux.marge >= 0 ? 'var(--green2)' : '#ef4444' },
                      { lbl: 'Taux de marge', val: totaux.revenu > 0 ? (totaux.marge / totaux.revenu * 100).toFixed(1) + ' %' : '—', color: 'var(--text)' },
                    ].map((k, i) => (
                      <div key={i} className="stat-card">
                        <div className="val" style={{ color: k.color, fontSize: 22 }}>{k.val}</div>
                        <div className="lbl">{k.lbl}</div>
                        {k.sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{k.sub}</div>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Breakdown par catégorie */}
                {breakdown && (
                  <div className="card" style={{ marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>Détail par catégorie</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                      {[
                        { key: 'prod',  label: 'Production',      icon: '🔧', color: '#6366f1', bgColor: 'rgba(99,102,241,0.08)' },
                        { key: 'sav',   label: 'SAV',             icon: '🛠',  color: '#f59e0b', bgColor: 'rgba(245,158,11,0.08)' },
                        { key: 'vt',    label: 'Visite Technique', icon: '👁',  color: '#10b981', bgColor: 'rgba(16,185,129,0.08)' },
                        { key: 'devis', label: 'Devis',           icon: '📋', color: '#3b82f6', bgColor: 'rgba(59,130,246,0.08)' },
                      ].map(({ key, label, icon, color, bgColor }) => {
                        const d = breakdown[key];
                        const cat = CATEGORIES[key];
                        const note = key === 'devis'
                          ? 'Les devis forfaitaires, comptés à part du prix de l’intervention.'
                          : key === 'sav'
                          ? 'Les SAV et les raccordements refaits après SAV, prix de l’intervention et plus-value.'
                          : 'Prix de l’intervention et plus-value, hors devis.';
                        return (
                          <div key={key} onClick={() => ouvrirDetail(label, note, it => !it.is_duplicate && cat.f(it))}
                            title="Voir la liste des interventions"
                            style={{ background: bgColor, border: `1px solid ${color}22`, borderRadius: 10, padding: '14px 16px', cursor: 'pointer' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                              <span style={{ fontSize: 16 }}>{icon}</span>
                              <span style={{ fontWeight: 700, fontSize: 13, color }}>{label}</span>
                              <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text3)' }}>voir ›</span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                                  Interventions
                                  {d.payees !== d.count && (
                                    <span style={{ display: 'block', fontSize: 10 }}>dont {d.payees} facturée{d.payees > 1 ? 's' : ''}</span>
                                  )}
                                </span>
                                <span style={{ fontWeight: 800, fontSize: 18, color }}>{d.count}</span>
                              </div>
                              <div style={{ height: 1, background: `${color}22` }} />
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                <span style={{ fontSize: 11, color: 'var(--text3)' }}>Revenu Covage</span>
                                <span style={{ fontWeight: 700, fontSize: 13, color }}>{fmt(d.revenu)}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                <span style={{ fontSize: 11, color: 'var(--text3)' }}>Coût techniciens</span>
                                <span style={{ fontSize: 12, color: 'var(--text2)' }}>{fmt(d.cout)}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                <span style={{ fontSize: 11, color: 'var(--text3)' }}>Marge</span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: (d.revenu - d.cout) >= 0 ? 'var(--green2)' : '#ef4444' }}>
                                  {fmt(d.revenu - d.cout)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Ce qu'il faut savoir avant de signer l'attachement. Le
                    devis est désormais compté — 50 % au sous-traitant s'il y a
                    eu nacelle ou nuit, 80 % sinon — mais son poids mérite
                    d'être dit : c'est lui qui fait la marge du mois. */}
                {signaux && (() => {
                  const sansTarif = Object.entries(signaux.sans_tarif || {});
                  const lignes = [];
                  if (signaux.devis && signaux.devis.nb > 0) lignes.push({
                    k: 'devis', f: it => paye(it) && it.is_devis && it.famille !== 'VT', t: `${signaux.devis.nb} devis forfaitaire${signaux.devis.nb > 1 ? 's' : ''} : ${fmt(signaux.devis.montant)} facturés, ${fmt(signaux.devis.tech)} pour les sous-traitants`,
                    d: `Le montant facturé est celui du CRM, pas celui du compte rendu : le technicien pose les lignes, la conduite d'activité arrête le prix après coup. Le devis se facture en plus du raccordement, et le sous-traitant en touche 80 %, ou 50 % s'il a fallu une nacelle, travailler de nuit ou hydrocurer un fourreau${signaux.devis.moitie ? ` (${signaux.devis.moitie} dans ce cas)` : ''}.`,
                  });
                  if (signaux.devis && signaux.devis.sur_vt > 0) lignes.push({
                    k: 'devisvt', f: it => paye(it) && it.is_devis && it.famille === 'VT', t: `${signaux.devis.sur_vt} devis chiffré${signaux.devis.sur_vt > 1 ? 's' : ''} pendant une visite technique, en attente : ${fmt(signaux.devis.attente_vt || 0)}`,
                    d: 'Une visite ne se facture jamais en devis. Le devis qu’elle produit se facture à la réalisation des '
                     + 'travaux, sur le compte rendu du raccordement — il entrera dans le mois où les travaux seront faits.',
                  });
                  if (signaux.devis && signaux.devis.non_facture > 0) lignes.push({
                    k: 'devisnf',
                    f: it => it.devis_non_facture,
                    t: `${signaux.devis.non_facture} devis chiffré${signaux.devis.non_facture > 1 ? 's' : ''} dans le CRM et non facturé${signaux.devis.non_facture > 1 ? 's' : ''} : ${fmt(signaux.devis.non_facture_montant)}`,
                    d: 'Le CRM porte un montant, la ligne n’en facture rien : intervention en échec — le devis '
                     + 'attend l’accord et se facturera au mois des travaux —, clôture remplacée, ou case décochée '
                     + 'alors que le prix est saisi. C’est la première chose à regarder quand notre total ne tombe '
                     + 'pas sur celui de Covage : ces montants pèsent souvent plus que les devis facturés.'
                     + (signaux.devis.non_facture_repris
                       ? ` ${signaux.devis.non_facture_repris} échec${signaux.devis.non_facture_repris > 1 ? 's' : ''} `
                         + 'ne compte pas ici : la commande a été reprise et son devis facturé le mois même — '
                         + 'le devis vit sur la commande, pas sur le work order, donc chaque tentative en porte '
                         + 'le montant et le compter deux fois gonflerait l’écart.'
                       : ''),
                  });
                  if (signaux.devis && signaux.devis.sans_montant > 0) lignes.push({
                    k: 'devis0', f: it => paye(it) && it.devis_sans_montant,
                    t: `${signaux.devis.sans_montant} devis coché${signaux.devis.sans_montant > 1 ? 's' : ''} dans le CRM sans montant`,
                    d: 'La case est posée, le prix pas encore. Ces interventions sont facturées normalement, '
                     + 'devis à part : un devis sans montant est une fiche inachevée, pas un devis à zéro euro — '
                     + 'le compter comme tel effacerait le prix de l’acte sur un SAV et la plus-value sur un '
                     + 'raccordement. Le montant se saisit dans Zoho, et le revenu arrivera au recalcul.',
                  });
                  if (signaux.devis && signaux.devis.desaccord_zoho > 0) lignes.push({
                    k: 'deviszoho', f: it => paye(it) && !!it.is_devis !== !!it.devis_cr,
                    t: `${signaux.devis.desaccord_zoho} devis où le technicien et le CRM ne disent pas la même chose`,
                    d: 'Même règle que la plus-value : c’est le CRM qui facture. Notre index fige ce qui était vrai '
                     + 'au dépôt du compte rendu, et le devis se chiffre après — 268 devis de l’historique étaient '
                     + 'à zéro chez nous alors que Zoho les chiffrait.',
                  });
                  if (signaux.fpv && signaux.fpv.sur_pto > 0) lignes.push({
                    // Le même périmètre que le compteur, qui ne compte que les
                    // lignes facturables : six autres PTO portent la case sur
                    // une clôture qui ne se paie pas, et la liste aurait
                    // annoncé dix là où le titre dit quatre.
                    k: 'fpvpto', f: it => paye(it) && it.fpv_sur_pto,
                    t: `${signaux.fpv.sur_pto} plus-value${signaux.fpv.sur_pto > 1 ? 's' : ''} cochée${signaux.fpv.sur_pto > 1 ? 's' : ''} sur une création de PTO, non facturée${signaux.fpv.sur_pto > 1 ? 's' : ''}`,
                    d: 'Un PTO_C ne peut pas porter de forfait : la plus-value ne s’y applique jamais, quoi que '
                     + 'dise la case du CRM. Elle n’est ni facturée à Covage ni reversée au sous-traitant. '
                     + 'La case est à décocher dans Zoho — quatre PTO de 2026 avaient été facturés 140 € de '
                     + 'plus-value sur un acte à 98 €, soit 560 € annoncés et 320 € reversés.',
                  });
                  if (signaux.fpv && signaux.fpv.nb > 0) lignes.push({
                    k: 'fpv', f: it => paye(it) && it.prix_fpv_covage > 0, t: `${signaux.fpv.nb} FPV au forfait : ${fmt(signaux.fpv.montant)}`,
                    d: 'C’est la case du CRM qui fait foi, au tarif de la grille (140 € / 80 € depuis le nouveau contrat Covage). Les 250 € encore inscrits dans Zoho sont l’ancien prix.',
                  });
                  if (sansTarif.length > 0) lignes.push({
                    k: 'tarif', f: it => it.tarif_manquant, t: `${sansTarif.reduce((a, [, n]) => a + n, 0)} intervention(s) sans tarif : ${sansTarif.map(([ty, n]) => `${ty} ×${n}`).join(', ')}`,
                    d: 'Aucune ligne de la grille ne correspond : revenu et coût comptent 0 €. À créer dans l’onglet Tarifs.',
                  });
                  if (signaux.vt && signaux.vt.nb > 0) lignes.push({
                    k: 'vt', f: it => it.type_cr === 'VT', t: `${signaux.vt.nb} visite${signaux.vt.nb > 1 ? 's' : ''} technique au tarif VT`,
                    d: `Une VT porte la typologie du raccordement à venir ; elle est désormais payée au tarif VT et non à celui du raccordement${signaux.vt.sans_resultat ? ` (dont ${signaux.vt.sans_resultat} sans résultat renseigné, comptée${signaux.vt.sans_resultat > 1 ? 's' : ''} comme faite${signaux.vt.sans_resultat > 1 ? 's' : ''})` : ''}.`,
                  });
                  if (signaux.fpv && signaux.fpv.desaccord_zoho > 0) lignes.push({
                    k: 'fpvzoho', f: it => paye(it) && !!it.has_fpv !== !!it.fpv_cr,
                    t: `${signaux.fpv.desaccord_zoho} plus-value${signaux.fpv.desaccord_zoho > 1 ? 's' : ''} où le technicien et le CRM ne disent pas la même chose`,
                    d: 'C’est le CRM qui facture : le technicien peut se tromper, la conduite d’activité relit et '
                     + 'pose le cas réel. Ces lignes valent le coup d’œil quand même — la case se coche désormais '
                     + 'toute seule d’après les longueurs de câble, et l’écart devrait se tarir. Elle reste '
                     + 'modifiable depuis la fiche du dossier.',
                  });
                  if (signaux.fpv && signaux.fpv.remplace_par_devis > 0) lignes.push({
                    k: 'fpvdevis', f: it => paye(it) && it.has_fpv && it.is_devis && !it.prix_fpv_covage, t: `${signaux.fpv.remplace_par_devis} FPV non facturé${signaux.fpv.remplace_par_devis > 1 ? 's' : ''} : un devis était coché`,
                    d: 'Le devis remplace la plus-value. Quand les deux cases sont cochées sur le même compte rendu, le FPV ne se facture pas et ne se reverse pas.',
                  });
                  if (signaux.doublons > 0) lignes.push({
                    k: 'dup', f: it => it.is_duplicate && !it.doublon_commande && !it.vt_remplacee, t: `${signaux.doublons} doublon${signaux.doublons > 1 ? 's' : ''} de work order exclu${signaux.doublons > 1 ? 's' : ''} des totaux`,
                    d: 'Deux comptes rendus facturables sur le même work order : seule la dernière clôture est payée, '
                     + 'comme chez Covage.',
                  });
                  if (signaux.doublons_commande > 0) lignes.push({
                    k: 'dupo', f: it => it.doublon_commande, t: `${signaux.doublons_commande} intervention${signaux.doublons_commande > 1 ? 's' : ''} déjà facturée${signaux.doublons_commande > 1 ? 's' : ''} sur la même commande`,
                    d: 'Une commande O ne se facture qu’une fois : Covage paie le raccordement, pas les passages qu’il a fallu. '
                     + 'La dernière clôture en CLEARED fait foi, y compris quand elle tombe sur un mois suivant — c’est elle qui '
                     + 'dit le travail livré. Le sous-traitant qui retourne sur une commande déjà facturée n’est pas payé : le '
                     + 'second passage est de sa responsabilité. Les SAV, qui ont chacun leur work order, se paient tous.',
                  });
                  if (signaux.vt_remplacees && signaux.vt_remplacees.nb > 0) lignes.push({
                    k: 'vtrac', f: it => it.vt_remplacee, t: `${signaux.vt_remplacees.nb} visite${signaux.vt_remplacees.nb > 1 ? 's' : ''} technique non facturée${signaux.vt_remplacees.nb > 1 ? 's' : ''} : la commande a été raccordée`,
                    d: 'Le technicien a jugé qu’on pouvait raccorder directement, sans devis : la commande se paie en '
                     + 'raccordement, un point. Une visite reste due quand elle a produit un devis, puisque la réalisation '
                     + 'se facture à part.',
                  });
                  const savInconnu = Object.entries(signaux.sav_delai_inconnu || {});
                  if (savInconnu.length > 0) lignes.push({
                    k: 'savdelai', f: it => it.sav_delai_inconnu, t: `${savInconnu.reduce((a, [, n]) => a + n, 0)} SAV sans délai reconnu : ${savInconnu.map(([ty, n]) => `${ty} ×${n}`).join(', ')}`,
                    d: 'Le prix d’un SAV dépend de son délai — 96 h, 48 h, 24 h, prévisite — lu sur la fiche Airtable. '
                     + 'Ces types-là n’en annoncent aucun : ils comptent au tarif SAV général de 90 €. Une reconstruction '
                     + 'se paie au prix du 96 h, et une mauvaise MES ne se facture pas — c’est notre production qu’on reprend.',
                  });
                  const savTech = Object.entries(signaux.sav_tech_a_confirmer || {});
                  if (savTech.length > 0) lignes.push({
                    k: 'savtech', f: it => it.tarif_tech_a_confirmer, t: `${savTech.reduce((a, [, n]) => a + n, 0)} SAV payés à un tarif sous-traitant provisoire : ${savTech.map(([ty, n]) => `${ty} ×${n}`).join(', ')}`,
                    d: 'Le prix facturé à Covage est celui du contrat ; le reversement au sous-traitant reste à fixer et vaut '
                     + 'encore 45 € par défaut. La marge affichée sur ces lignes est donc trop belle. À corriger dans l’onglet Tarifs.',
                  });
                  if (!lignes.length) return null;
                  return (
                    <div className="card" style={{ marginBottom: 20 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>À savoir sur ces chiffres</div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
                        Ce que la grille tarifaire ne sait pas compter sur cette période.
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {lignes.map(l => (
                          <div key={l.k} style={{ display: 'flex', gap: 10, alignItems: 'flex-start',
                            padding: '9px 12px', background: 'var(--bg3)', borderRadius: 8 }}>
                            <span style={{ fontSize: 13, lineHeight: 1.2 }}>•</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 12, fontWeight: 600 }}>
                                {l.t}
                                {l.f && <BtnListe onClick={() => ouvrirDetail(l.t, l.d, l.f)} />}
                              </div>
                              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{l.d}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Le CRM dit raccordement, Airtable facture un SAV.
                    On paie le SAV — on ne reverse pas un raccordement que le
                    Front ne nous facture pas comme tel — et les deux prix sont
                    montrés : le reversement retenu, et le revenu qu'on
                    n'encaisse pas. Ce second chiffre sert à relire
                    l'attachement du Front, qui a peut-être simplement oublié
                    de nous payer un raccordement suite SAV. */}
                {signaux && (signaux.sav_racc_a_verifier || []).length > 0 && (() => {
                  const liste = signaux.sav_racc_a_verifier;
                  const retenu = liste.reduce((a, l) => a + (l.prix_si_racc - l.prix_paye), 0);
                  const manque = liste.reduce((a, l) => a + (l.covage_si_racc - l.covage_paye), 0);
                  return (
                    <div className="card" style={{ marginBottom: 20, borderLeft: '3px solid var(--amber)' }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                        {liste.length} SAV où le CRM et Airtable se contredisent
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12, lineHeight: 1.5 }}>
                        Le CRM annonce un raccordement suite SAV, Airtable le facture en SAV. Ces lignes
                        sont payées <strong>au tarif SAV</strong> : on ne reverse pas le prix d'un
                        raccordement que le Front ne nous facture pas comme tel.
                        {' '}<strong>{fmt(retenu)}</strong> retenus au sous-traitant,
                        {' '}<strong>{fmt(manque)}</strong> de revenu non encaissé.
                        <br />
                        Deux lectures : soit la typologie du CRM est à corriger, soit le Front a oublié
                        de nous payer le raccordement — à vérifier sur son attachement.
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ background: 'var(--bg3)' }}>
                              {['Compte', 'Commande', 'Work order', 'Typologie CRM', 'Facturation Airtable',
                                'Technicien', 'Covage', 'Commentaire interne'].map((h, i) => (
                                <th key={h} style={{ padding: '8px 12px', textAlign: i > 4 && i < 7 ? 'right' : 'left',
                                  fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em',
                                  color: 'var(--text3)', whiteSpace: 'nowrap' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {liste.map((l, i) => (
                              <tr key={l.ref_work_order + '-' + i} style={{ borderTop: '1px solid var(--border)' }}>
                                <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                                  {String(l.compte || '').replace('VALEA-', '')}
                                </td>
                                <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{l.ref_commande || '—'}</td>
                                <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{l.ref_work_order}</td>
                                <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: 'var(--amber)' }}>
                                  {l.typo_crm}
                                </td>
                                <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{l.facturation}</td>
                                <td style={{ padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap',
                                  fontVariantNumeric: 'tabular-nums' }}>
                                  {fmt(l.prix_paye)}
                                  <span style={{ color: 'var(--text3)' }}> au lieu de {fmt(l.prix_si_racc)}</span>
                                </td>
                                <td style={{ padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap',
                                  fontVariantNumeric: 'tabular-nums' }}>
                                  {fmt(l.covage_paye)}
                                  <span style={{ color: 'var(--text3)' }}> au lieu de {fmt(l.covage_si_racc)}</span>
                                </td>
                                <td style={{ padding: '8px 12px', color: 'var(--text2)', minWidth: 220 }}>
                                  {l.commentaire_interne || '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {/* Combien de chaque chose, et pour quel montant.
                    Le prix de l'acte est tenu à part du devis et de la
                    plus-value : un seul devis noyait une ligne entière — sept
                    visites techniques affichaient 2 189 € parce que l'une
                    portait un devis de 1 524,60 €, quand les visites
                    elles-mêmes font 7 × 95 = 665 €. */}
                {typologies && typologies.length > 0 && (
                  <div className="card" style={{ marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Par typologie</div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
                      Interventions clôturées et payées. Le devis et la plus-value sont comptés à part :
                      la colonne « acte » est le prix de l'intervention seule.
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: 'var(--bg3)' }}>
                            {['Typologie', 'Nb', 'Acte', 'Devis', 'Plus-value', 'Revenu Covage', 'Coût tech.', 'Marge'].map((h, i) => (
                              <th key={h} style={{ padding: '8px 14px', textAlign: i === 0 ? 'left' : 'right',
                                fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text3)' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {['RAC', 'SAV', 'VT'].map(fam => {
                            const groupe = typologies.filter(t => t.famille === fam);
                            if (!groupe.length) return null;
                            const tot = groupe.reduce((a, t) => ({
                              nb: a.nb + t.nb, acte: a.acte + t.revenu_acte, devis: a.devis + t.revenu_devis,
                              fpv: a.fpv + t.revenu_fpv, revenu: a.revenu + t.revenu, cout: a.cout + t.cout,
                            }), { nb: 0, acte: 0, devis: 0, fpv: 0, revenu: 0, cout: 0 });
                            const titre = fam === 'RAC' ? 'Raccordements'
                              : fam === 'SAV' ? 'SAV et reprises après SAV' : 'Visites techniques';
                            return (
                              <React.Fragment key={fam}>
                                <tr>
                                  <td colSpan={8} style={{ padding: '12px 14px 5px', fontSize: 11, fontWeight: 700,
                                    textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text3)' }}>{titre}</td>
                                </tr>
                                {groupe.map(t => {
                                  // Les lignes de cette typologie : la meme cle que le serveur a
                                  // utilisee pour les regrouper.
                                  const dansLaTypo = it => paye(it) && (it.tarif_cle || it.typo || '(typologie vide)') === t.cle;
                                  const nom = t.label + (t.cle !== t.label ? ' (' + t.cle + ')' : '');
                                  return (
                                  <tr key={t.cle} style={{ borderTop: '1px solid var(--border)' }}>
                                    <td style={{ padding: '7px 14px' }}>
                                      {t.label}
                                      {t.cle !== t.label && (
                                        <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', marginLeft: 7 }}>{t.cle}</span>
                                      )}
                                    </td>
                                    <td style={{ padding: '7px 14px', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                      {t.nb}
                                      <BtnListe onClick={() => ouvrirDetail(nom, 'Toutes les interventions de cette typologie, payees.', dansLaTypo)} />
                                    </td>
                                    <td style={{ padding: '7px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(t.revenu_acte)}</td>
                                    <td style={{ padding: '7px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: t.revenu_devis ? 'var(--text)' : 'var(--text3)' }}>
                                      {t.revenu_devis ? fmt(t.revenu_devis) : '—'}
                                      {t.nb_devis > 0 && <span style={{ fontSize: 10, color: 'var(--text3)' }}>{' ×' + t.nb_devis}</span>}
                                      {t.nb_devis > 0 && <BtnListe onClick={() => ouvrirDetail(nom + ' — devis', 'Les interventions de cette typologie qui portent un devis forfaitaire.', it => dansLaTypo(it) && it.montant_devis > 0)} />}
                                    </td>
                                    <td style={{ padding: '7px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: t.revenu_fpv ? 'var(--text)' : 'var(--text3)' }}>
                                      {t.revenu_fpv ? fmt(t.revenu_fpv) : '—'}
                                      {t.nb_fpv > 0 && <span style={{ fontSize: 10, color: 'var(--text3)' }}>{' ×' + t.nb_fpv}</span>}
                                      {t.nb_fpv > 0 && <BtnListe onClick={() => ouvrirDetail(nom + ' — plus-value', 'Les interventions de cette typologie dont la case FPV est cochee et facturee.', it => dansLaTypo(it) && it.prix_fpv_covage > 0)} />}
                                    </td>
                                    <td style={{ padding: '7px 14px', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmt(t.revenu)}</td>
                                    <td style={{ padding: '7px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(t.cout)}</td>
                                    <td style={{ padding: '7px 14px', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                                      color: t.marge >= 0 ? 'var(--green2)' : '#ef4444' }}>{fmt(t.marge)}</td>
                                  </tr>
                                  );
                                })}
                                <tr style={{ borderTop: '1px solid var(--border)', background: 'var(--bg3)' }}>
                                  <td style={{ padding: '7px 14px', fontSize: 12, fontWeight: 700 }}>Sous-total {titre.toLowerCase()}</td>
                                  <td style={{ padding: '7px 14px', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                    {tot.nb}
                                    <BtnListe onClick={() => ouvrirDetail(titre, 'Toutes les interventions payées de cette famille.', it => paye(it) && it.famille === fam)} />
                                  </td>
                                  <td style={{ padding: '7px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(tot.acte)}</td>
                                  <td style={{ padding: '7px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{tot.devis ? fmt(tot.devis) : '—'}</td>
                                  <td style={{ padding: '7px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{tot.fpv ? fmt(tot.fpv) : '—'}</td>
                                  <td style={{ padding: '7px 14px', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(tot.revenu)}</td>
                                  <td style={{ padding: '7px 14px', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(tot.cout)}</td>
                                  <td style={{ padding: '7px 14px', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                                    color: tot.revenu - tot.cout >= 0 ? 'var(--green2)' : '#ef4444' }}>{fmt(tot.revenu - tot.cout)}</td>
                                </tr>
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Résumé par technicien */}
                {summary.length > 0 && (
                  <div className="card" style={{ marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>Résumé par compte</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 14, lineHeight: 1.55 }}>
                      Le <strong>net à verser</strong> est le coût technicien moins les malus retenus — ceux
                      qui ont été annoncés au technicien et pas retirés. Un malus dont la commande a été
                      reclôturée un autre mois n’a pas de ligne ici mais compte dans le total du compte, et
                      apparaît dans le classeur. La marge ne bouge pas : le malus traverse, Covage le
                      retient d’un côté et il se récupère de l’autre. Le détail est dans la{' '}
                      <a href="/malus" style={{ color: 'var(--blue2)' }}>page Malus</a>.
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: 'var(--bg3)' }}>
                            {['Compte', 'Technicien(s)', 'Interventions', 'CLEARED', 'FAILED', 'En cours', 'Revenu Covage', 'Coût tech.', 'Malus', 'Net à verser', 'Marge', '% marge'].map(h => (
                              <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 700, fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .4, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {summary.map((s, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', background: i % 2 === 0 ? 'var(--bg2)' : 'transparent' }}
                              onClick={() => setFilterCompte(filterCompte === s.compte ? '' : s.compte)}>
                              <td style={{ padding: '10px 14px', fontWeight: 700 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  {filterCompte === s.compte && <span style={{ color: 'var(--blue2)', fontSize: 11 }}>●</span>}
                                  <span style={{ fontFamily: 'var(--mono)' }}>{s.ref || '—'}</span>
                                  {!s.actif && (
                                    <span title="Compte fermé : son travail reste dû"
                                      style={{ fontSize: 9, fontWeight: 700, color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 5, padding: '1px 5px' }}>fermé</span>
                                  )}
                                </div>
                                {s.du && s.au && s.du !== s.au && (
                                  <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 400, marginTop: 2 }}>
                                    {'du ' + s.du.slice(8) + '/' + s.du.slice(5, 7) + ' au ' + s.au.slice(8) + '/' + s.au.slice(5, 7)}
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text2)' }}>
                                {(s.titulaires || []).map(t => t.nom).join(', puis ') || '—'}
                              </td>
                              <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600 }}>{s.total}</td>
                              <td style={{ padding: '10px 14px', textAlign: 'center', color: '#16a34a', fontWeight: 700 }}>{s.cleared}</td>
                              <td style={{ padding: '10px 14px', textAlign: 'center', color: '#dc2626' }}>{s.failed}</td>
                              <td style={{ padding: '10px 14px', textAlign: 'center', color: '#b45309' }}>{s.en_cours}</td>
                              <td style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--blue2)', whiteSpace: 'nowrap' }}>{fmt(s.revenu)}</td>
                              <td style={{ padding: '10px 14px', color: '#f59e0b', whiteSpace: 'nowrap' }}>{fmt(s.cout)}</td>
                              <td title={s.malus ? s.malus_nb + ' malus retenu(s) sur la période' : 'Aucun malus retenu'}
                                style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: s.malus ? '#ef4444' : 'var(--text3)' }}>
                                {s.malus ? '− ' + fmt(s.malus) : '—'}
                              </td>
                              <td style={{ padding: '10px 14px', fontWeight: 700, whiteSpace: 'nowrap', color: '#f59e0b' }}>
                                {fmt(s.net !== undefined ? s.net : s.cout)}
                              </td>
                              <td style={{ padding: '10px 14px', fontWeight: 700, whiteSpace: 'nowrap', color: s.marge >= 0 ? 'var(--green2)' : '#ef4444' }}>{fmt(s.marge)}</td>
                              <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: 'var(--text2)' }}>
                                {s.revenu > 0 ? (s.marge / s.revenu * 100).toFixed(1) + ' %' : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        {summary.length > 1 && totaux && (
                          <tfoot>
                            <tr style={{ background: 'var(--bg3)', fontWeight: 800, borderTop: '2px solid var(--border)' }}>
                              <td style={{ padding: '10px 14px' }}>TOTAL</td>
                              <td />
                              <td style={{ padding: '10px 14px', textAlign: 'center' }}>{totaux.nb_total}</td>
                              <td style={{ padding: '10px 14px', textAlign: 'center', color: '#16a34a' }}>{totaux.nb_cleared}</td>
                              <td colSpan={2} />
                              <td style={{ padding: '10px 14px', color: 'var(--blue2)', whiteSpace: 'nowrap' }}>{fmt(totaux.revenu)}</td>
                              <td style={{ padding: '10px 14px', color: '#f59e0b', whiteSpace: 'nowrap' }}>{fmt(totaux.cout)}</td>
                              <td style={{ padding: '10px 14px', color: totaux.malus ? '#ef4444' : 'var(--text3)', whiteSpace: 'nowrap' }}>
                                {totaux.malus ? '− ' + fmt(totaux.malus) : '—'}
                              </td>
                              <td style={{ padding: '10px 14px', color: '#f59e0b', whiteSpace: 'nowrap' }}>{fmt(totaux.net)}</td>
                              <td style={{ padding: '10px 14px', color: totaux.marge >= 0 ? 'var(--green2)' : '#ef4444', whiteSpace: 'nowrap' }}>{fmt(totaux.marge)}</td>
                              <td style={{ padding: '10px 14px', color: 'var(--text2)' }}>
                                {totaux.revenu > 0 ? (totaux.marge / totaux.revenu * 100).toFixed(1) + ' %' : '—'}
                              </td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                    {filterCompte && (
                      <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text3)' }}>
                        Filtre actif : <strong style={{ color: 'var(--blue2)', fontFamily: 'var(--mono)' }}>{String(filterCompte).replace('VALEA-', '')}</strong>
                        <button className="btn btn-ghost btn-sm" style={{ marginLeft: 8 }} onClick={() => setFilterCompte('')}>✕ Supprimer le filtre</button>
                      </div>
                    )}
                  </div>
                )}

                {/* Un classeur par société, sans relancer la recherche.
                    Une facture se fait par entreprise, et le classeur global
                    les mélange : le découper à la main était le vrai travail. */}
                {entreprisesDuResultat.length > 0 && (
                  <div className="card" style={{ marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                      Attachement par entreprise
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12, lineHeight: 1.5 }}>
                      Un classeur par société, sur la même période, sans relancer la recherche.
                      Le filtre de statut est appliqué ; celui d'un compte précis ne l'est pas.
                      {comptesSansEntreprise.length > 0 && (
                        <>
                          <br />
                          {comptesSansEntreprise.length} compte{comptesSansEntreprise.length > 1 ? 's' : ''} sans
                          entreprise renseignée — {comptesSansEntreprise.map(c => String(c).replace('VALEA-', '')).join(', ')}
                          {' '}— ne figure{comptesSansEntreprise.length > 1 ? 'nt' : ''} que dans le classeur global.
                          Le rattachement se fait sur la page Techniciens.
                        </>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {entreprisesDuResultat.map(ent => (
                        <button key={ent.nom} onClick={() => exporterXlsx(ent)} disabled={exportEnCours}
                          title={ent.liste.map(c => String(c).replace('VALEA-', '')).join(', ')}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '7px 14px', borderRadius: 8,
                            border: '1px solid rgba(16,185,129,0.4)',
                            background: 'rgba(16,185,129,0.1)', color: '#059669',
                            fontWeight: 700, fontSize: 12,
                            cursor: exportEnCours ? 'default' : 'pointer',
                            opacity: exportEnCours && exportCible !== ent.nom ? 0.4 : 1,
                          }}>
                          <span>{exportCible === ent.nom ? '⏳' : '📥'}</span>
                          <span>{ent.nom}</span>
                          <span style={{ fontWeight: 400, color: 'var(--text3)', fontVariantNumeric: 'tabular-nums' }}>
                            {fmt(ent.net)}
                          </span>
                          <span style={{ fontWeight: 400, fontSize: 10, color: 'var(--text3)' }}>
                            {ent.liste.length} compte{ent.liste.length > 1 ? 's' : ''}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tableau détail */}
                <div className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>
                      Détail des interventions
                      <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 400, color: 'var(--text3)' }}>
                        ({filteredItems.length} / {items.length})
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button onClick={exporterXlsx} disabled={exportEnCours || !items || filteredItems.length === 0}
                        style={{
                          padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(16,185,129,0.4)',
                          background: 'rgba(16,185,129,0.1)', color: '#059669', fontWeight: 700,
                          fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', opacity: (!items || filteredItems.length === 0) ? 0.4 : 1,
                        }}>
                        {exportEnCours ? '⏳ Construction…' : '📥 Exporter Excel'}
                      </button>
                      {nbDoublons > 0 && (
                        <span style={{ fontSize: 11, background: 'rgba(245,158,11,0.15)', color: '#b45309', borderRadius: 6, padding: '3px 9px', fontWeight: 700, border: '1px solid rgba(245,158,11,0.3)' }}>
                          ⚠ {nbDoublons} doublon{nbDoublons > 1 ? 's' : ''} exclu{nbDoublons > 1 ? 's' : ''} des totaux
                        </span>
                      )}
                      <select value={filterResultat} onChange={e => setFilterResultat(e.target.value)}
                        className="input" style={{ fontSize: 12, padding: '5px 10px', width: 160 }}>
                        <option value="">Tous les statuts</option>
                        <option value="CLEARED">CLEARED</option>
                        <option value="FAILED">FAILED</option>
                        <option value="en_cours">En cours</option>
                        <option value="doublon">Doublons</option>
                      </select>
                    </div>
                  </div>

                  {filteredItems.length === 0 ? (
                    <div className="empty"><div className="icon">🔍</div><p>Aucune intervention trouvée</p></div>
                  ) : (
                    <div style={{ overflowX: 'auto', maxHeight: 560, overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: 'var(--bg3)', position: 'sticky', top: 0, zIndex: 1 }}>
                            {['Clôturé le', 'Compte', 'Réf commande', 'Work Order', 'Type', 'Statut', 'Revenu Covage', 'Coût tech.', 'Marge'].map(h => (
                              <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .4, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredItems.map((it, i) => {
                            const rs = RESULTAT_STYLE[it.resultat] || RESULTAT_STYLE.en_cours;
                            const isDup = it.is_duplicate === true;
                            const rowBg = isDup
                              ? 'rgba(245,158,11,0.08)'
                              : i % 2 === 0 ? 'var(--bg2)' : 'transparent';
                            return (
                              <tr key={i} style={{ background: rowBg, borderBottom: isDup ? '1px solid rgba(245,158,11,0.25)' : '1px solid var(--border)' }}>
                                <td style={{ padding: '7px 12px', whiteSpace: 'nowrap', fontFamily: 'var(--mono)', fontSize: 11 }}>
                                  {fmtDate(it.date)}
                                  {/* Le jour de l'intervention, quand il n'est pas celui de la clôture : c'est la clôture qui fait le mois de facturation. */}
                                  {it.date_depot && it.date_depot !== it.date && (
                                    <div style={{ fontSize: 10, color: 'var(--text3)' }} title="Jour de l'intervention — la facturation suit la clôture">itv {fmtDate(it.date_depot)}</div>
                                  )}
                                </td>
                                <td style={{ padding: '7px 12px', whiteSpace: 'nowrap' }}>
                                  <span style={{ fontWeight: 700, fontFamily: 'var(--mono)' }}>{it.ref_compte || '—'}</span>
                                  {it.technicien && (
                                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>{it.technicien}</div>
                                  )}
                                </td>
                                <td style={{ padding: '7px 12px', whiteSpace: 'nowrap', fontFamily: 'var(--mono)', fontSize: 11 }}>
                                  {it.dossier_id ? (
                                    <a href={`/dossier/${it.dossier_id}`} target="_blank" rel="noreferrer"
                                      title="Ouvrir la fiche du dossier dans un nouvel onglet"
                                      style={{ color: isDup ? '#b45309' : 'var(--blue2)', textDecoration: 'none', borderBottom: '1px dotted currentColor' }}>
                                      {it.ref_commande || 'fiche'}
                                    </a>
                                  ) : (
                                    <span style={{ color: isDup ? '#b45309' : 'var(--blue2)' }}>{it.ref_commande || '—'}</span>
                                  )}
                                  {isDup && (
                                    <span title={it.vt_remplacee
                                      ? 'Cette commande a ete raccordee : la visite ne se facture pas, le technicien a juge qu on pouvait raccorder directement.'
                                      : it.doublon_commande
                                      ? 'Cette commande a deja ete facturee sur un autre work order : elle ne se facture qu une fois, a sa derniere cloture.'
                                      : 'Deux comptes rendus facturables sur le meme work order : seule la derniere cloture est payee.'}
                                      style={{ marginLeft: 6, background: 'rgba(245,158,11,0.2)', color: '#b45309', borderRadius: 5, padding: '1px 6px', fontSize: 9, fontWeight: 800, border: '1px solid rgba(245,158,11,0.4)', verticalAlign: 'middle' }}>
                                      {it.vt_remplacee ? 'VT NON DUE' : it.doublon_commande ? 'DEJA FACTUREE' : 'DOUBLON'}
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: '7px 12px', whiteSpace: 'nowrap', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{it.ref_work_order || '—'}</td>
                                <td style={{ padding: '7px 12px', whiteSpace: 'nowrap' }}>
                                  <span style={{ background: 'rgba(99,102,241,0.1)', color: '#6366f1', borderRadius: 6, padding: '2px 7px', fontSize: 10, fontWeight: 700 }}>
                                    {it.typo_label || it.typo || '—'}
                                  </span>
                                  {it.tarif_manquant && (
                                    <span title={'Aucun tarif pour « ' + (it.typo || '?') + ' » : compté 0 €. À créer dans l’onglet Tarifs.'}
                                      style={{ marginLeft: 4, background: 'rgba(239,68,68,0.12)', color: '#dc2626', borderRadius: 6, padding: '2px 6px', fontSize: 10, fontWeight: 800 }}>
                                      ⚠ sans tarif
                                    </span>
                                  )}
                                  {it.has_fpv && (
                                    <span title={it.prix_fpv_covage
                                      ? 'Plus-value cochée sur le compte rendu, au forfait de la grille : ' + fmt(it.prix_fpv_covage) + ' facturés, ' + fmt(it.prix_fpv_tech) + ' pour le technicien.'
                                      : 'Plus-value cochée, mais un devis l accompagne : le devis la remplace, elle n est ni facturée ni reversée.'}
                                      style={{ marginLeft: 4, background: it.prix_fpv_covage ? 'rgba(245,158,11,0.12)' : 'var(--bg3)', color: it.prix_fpv_covage ? '#b45309' : 'var(--text3)', borderRadius: 6, padding: '2px 6px', fontSize: 10, fontWeight: 700, textDecoration: it.prix_fpv_covage ? 'none' : 'line-through' }}>+FPV</span>
                                  )}
                                  {it.is_devis && (
                                    <span title={it.famille === 'VT'
                                      ? 'Devis chiffré pendant la visite : il se facturera à la réalisation des travaux, pas sur la visite.'
                                      : it.famille === 'SAV'
                                      ? 'Un SAV sur devis se paie au devis seul, moitié pour le sous-traitant, sans prix d’intervention par-dessus.'
                                      : it.devis_part === 0.5
                                      ? 'Moitié pour le sous-traitant : ' + [it.devis_nacelle && 'nacelle', it.devis_hno && 'travail de nuit', it.devis_hydro && 'hydrocurage'].filter(Boolean).join(', ') + '.'
                                      : 'Quatre cinquièmes pour le sous-traitant : ni nacelle, ni travail de nuit, ni hydrocurage.'}
                                      style={{ marginLeft: 4, background: it.famille === 'VT' ? 'var(--bg3)' : 'rgba(59,130,246,0.12)', color: it.famille === 'VT' ? 'var(--text3)' : '#2563eb', borderRadius: 6, padding: '2px 6px', fontSize: 10, fontWeight: 700 }}>
                                      DEVIS {it.montant_devis ? `${Number(it.montant_devis).toLocaleString('fr-FR')}€` : ''}
                                      {it.devis_part ? ` · ${Math.round(it.devis_part * 100)}%` : ''}
                                      {it.famille === 'VT' ? ' · en attente' : ''}
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: '7px 12px', whiteSpace: 'nowrap' }}>
                                  <span style={{ background: rs.bg, color: rs.color, borderRadius: 6, padding: '2px 7px', fontSize: 10, fontWeight: 700 }}>{rs.label}</span>
                                </td>
                                <td style={{ padding: '7px 12px', whiteSpace: 'nowrap', fontWeight: !isDup && it.prix_covage > 0 ? 700 : 400, color: !isDup && it.prix_covage > 0 ? 'var(--blue2)' : 'var(--text3)' }}>
                                  {!isDup && it.prix_covage > 0 ? fmt(it.prix_covage) : '—'}
                                </td>
                                <td style={{ padding: '7px 12px', whiteSpace: 'nowrap', color: !isDup && it.prix_tech > 0 ? '#f59e0b' : 'var(--text3)' }}>
                                  {!isDup && it.prix_tech > 0 ? fmt(it.prix_tech) : '—'}
                                </td>
                                <td style={{ padding: '7px 12px', whiteSpace: 'nowrap', fontWeight: 700, color: it.marge > 0 ? 'var(--green2)' : it.marge < 0 ? '#ef4444' : 'var(--text3)' }}>
                                  {!isDup && it.resultat === 'CLEARED' && it.prix_covage > 0 ? fmt(it.marge) : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}

            {!loading && items === null && (
              <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
                <p style={{ fontSize: 14 }}>Sélectionnez une période et cliquez sur <strong>Calculer</strong></p>
                <p style={{ fontSize: 12, marginTop: 6 }}>Configurez les tarifs dans l'onglet <strong>Tarifs</strong> avant de lancer le rapport.</p>
              </div>
            )}
          </>
        )}

        {/* ─── TAB TARIFS ─── */}
        {tab === 'tarifs' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Configuration des tarifs par type d'intervention</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                  Renseignez le prix que vous facture Covage et le prix que vous payez à vos techniciens pour chaque type d'intervention.
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {tarifsMsg && (
                  <span style={{ fontSize: 12, color: tarifsMsg.includes('✓') ? 'var(--green2)' : '#ef4444', fontWeight: 600 }}>{tarifsMsg}</span>
                )}
                <button className="btn btn-primary" onClick={saveTarifs} disabled={savingTarifs}>
                  {savingTarifs ? 'Enregistrement…' : '💾 Enregistrer'}
                </button>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--bg3)' }}>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .4, borderBottom: '1px solid var(--border)' }}>Type d'intervention</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .4, borderBottom: '1px solid var(--border)', width: 200 }}>Prix Covage (€ HT)</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .4, borderBottom: '1px solid var(--border)', width: 200 }}>Prix technicien (€ HT)</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .4, borderBottom: '1px solid var(--border)', width: 160 }}>Marge unitaire</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(localTarifs).map(([key, t], i) => {
                    const margeU = (t.prix_covage || 0) - (t.prix_tech || 0);
                    return (
                      <tr key={key} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'var(--bg2)' : 'transparent' }}>
                        <td style={{ padding: '10px 14px', fontWeight: 600 }}>
                          <div style={{ marginBottom: 2 }}>{t.label || key}</div>
                          <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{key}</div>
                        </td>
                        <td style={{ padding: '8px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <input
                              type="number" min="0" step="0.01"
                              value={t.prix_covage || 0}
                              onChange={e => updateLocalTarif(key, 'prix_covage', e.target.value)}
                              className="input"
                              style={{ width: 120, fontSize: 13, textAlign: 'right' }}
                            />
                            <span style={{ color: 'var(--text3)', fontSize: 12 }}>€</span>
                          </div>
                        </td>
                        <td style={{ padding: '8px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <input
                              type="number" min="0" step="0.01"
                              value={t.prix_tech || 0}
                              onChange={e => updateLocalTarif(key, 'prix_tech', e.target.value)}
                              className="input"
                              style={{ width: 120, fontSize: 13, textAlign: 'right' }}
                            />
                            <span style={{ color: 'var(--text3)', fontSize: 12 }}>€</span>
                          </div>
                        </td>
                        <td style={{ padding: '10px 14px', fontWeight: 700, color: margeU > 0 ? 'var(--green2)' : margeU < 0 ? '#ef4444' : 'var(--text3)' }}>
                          {margeU !== 0 ? fmt(margeU) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 20, padding: 14, background: 'var(--bg3)', borderRadius: 10, fontSize: 12, color: 'var(--text3)' }}>
              💡 Les montants sont comptabilisés pour les interventions <strong>CLEARED</strong> ;
              les <strong>FAILED</strong> et celles encore en cours n'entrent pas dans le calcul.
              Deux exceptions : une <strong>visite technique</strong> validée compte au tarif VT même si son résultat
              n'est pas renseigné — le formulaire VT ne le remplit pas — et un second compte rendu facturable
              sur le même work order est traité en <strong>doublon</strong>, donc non payé.
              <div style={{ marginTop: 10 }}>
                Le <strong>devis forfaitaire</strong> ne se règle pas à la grille : il se facture en plus du
                raccordement, et le sous-traitant en touche <strong>50 %</strong> s'il a fallu une nacelle,
                travailler de nuit ou hydrocurer un fourreau, <strong>80 %</strong> sinon — plus le prix du
                raccordement dans les deux cas. Ces cases viennent du compte rendu du technicien.
              </div>
            </div>
          </div>
        )}
      </div>

      <FenetreDetail detail={detail} onClose={() => setDetail(null)} />
    </>
  );
}
